import { Box, Button, Typography, makeStyles } from '@material-ui/core';
import {
  NavigateNext as NavigateNextIcon,
  NavigateBefore as NavigateBeforeIcon,
} from '@material-ui/icons';

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  SubtaskItemStatusLabel,
  SubtaskItemStatusType,
} from '../../../../constants/subtaskItemStatus';
import { ISubtaskItem } from '../../../../models/subtaskItem.model';
import { ISubtaskType } from '../../../../models/subtaskType.model';
import { AppState } from '../../../../state/configureStore';
import { updateSubtaskItemStatus } from '../../../../state/subtaskItem';
import { SubtaskTypeState } from '../../../../state/subtaskType';
import subtaskItemStatusRules from '../../../../utils/subtaskItemStatusRule';
import ArtworkLinkDialog from './ArtworkLinkDialog';
import SubtaskItemCloseDialog from './subtaskItem/SubtaskItemCloseDialog';
import SubtaskItemDoneDialog from './subtaskItem/SubtaskItemDoneDialog';
import SubtaskItemCustomerRejectDialog from './subtaskItem/SubtaskItemCustomerRejectDialog';

const useStyles = makeStyles((theme) => ({
  actionPanel: {
    padding: theme.spacing(1.5),
    borderRadius: 20,
    border: '1px solid rgba(37, 99, 235, 0.12)',
    background:
      'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.96) 100%)',
    boxShadow: '0 14px 32px rgba(15, 23, 42, 0.08)',
  },
  actionGroup: {
    '& + &': {
      marginTop: theme.spacing(1.5),
    },
  },
  actionGroupLabel: {
    marginBottom: theme.spacing(1),
    fontSize: '0.72rem',
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: '#64748b',
  },
  actionButtonRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  actionButton: {
    minHeight: 40,
    padding: theme.spacing(0.75, 1.5),
    borderRadius: 14,
    textTransform: 'none',
    fontWeight: 700,
    letterSpacing: 0,
    boxShadow: 'none',
    transition:
      'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease, background-color 180ms ease',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 10px 18px rgba(15, 23, 42, 0.1)',
    },
  },
  previousActionButton: {
    color: '#475569',
    background: 'rgba(241, 245, 249, 0.92)',
    border: '1px solid rgba(148, 163, 184, 0.2)',
    '&:hover': {
      background: '#ffffff',
    },
  },
  nextActionButton: {
    color: '#eff6ff',
    background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)',
    '&:hover': {
      background: 'linear-gradient(135deg, #1e40af 0%, #1d4ed8 100%)',
    },
  },
}));

interface ISubtaskItemActionMenuProps {
  subtaskItem: ISubtaskItem;
}

const SubtaskItemActionMenu: React.FC<ISubtaskItemActionMenuProps> = (props) => {
  const { subtaskItem } = props;
  console.log('=== render subtaskItem: ', subtaskItem);
  const dispatch = useDispatch();
  const classes = useStyles();

  const [openSubtaskItemCloseDialog, setOpenSubtaskItemCloseDialog] = useState<boolean>(
    false,
  );

  const [openSubtaskItemDoneDialog, setOpenSubtaskItemDoneDialog] = useState<boolean>(
    false,
  );

  const [
    openSubtaskItemCustRejectDialog,
    setOpenSubtaskItemCustRejectDialog,
  ] = useState<boolean>(false);

  const [openArtworkLinkDialog, setOpenArtworkLinkDialog] = useState<boolean>(false);
  const { items: subtaskTypeList } = useSelector<AppState, SubtaskTypeState>(
    (state) => state.subtaskType,
  );

  const subtaskTypeArtwork = subtaskTypeList.find(
    (subtaskType: ISubtaskType) => subtaskType.code === 'ARTWORK',
  );

  const onClickChangeStatus = (
    selectedSubtaskItem: ISubtaskItem,
    subtaskItemStatus: SubtaskItemStatusType,
  ) => {
    if (
      subtaskItemStatus === 'WAITING_FOR_SALES' &&
      subtaskTypeArtwork &&
      selectedSubtaskItem.smSubtask?.smSubtaskTypeId === subtaskTypeArtwork.id
    ) {
      setOpenArtworkLinkDialog(true);
    } else if (
      subtaskItemStatus !== 'CLOSE' &&
      subtaskItemStatus !== 'DONE' &&
      subtaskItemStatus !== 'CUSTOMER_REJECT'
    ) {
      const selectedSubtaskItemClone = { ...selectedSubtaskItem };
      selectedSubtaskItemClone.status = subtaskItemStatus;
      dispatch(updateSubtaskItemStatus(selectedSubtaskItemClone));
    } else if (subtaskItemStatus === 'CLOSE') {
      setOpenSubtaskItemCloseDialog(true);
    } else if (subtaskItemStatus === 'DONE') {
      setOpenSubtaskItemDoneDialog(true);
    }

    if (subtaskItemStatus === 'CUSTOMER_REJECT') {
      //open dialog to fill reason when change status to CUSTOMER_REJECT.
      setOpenSubtaskItemCustRejectDialog(true);
    }
  };

  const useCommonStatus = subtaskItem.smSubtask?.useCommonStatus;

  const subtaskItemStatusRule = subtaskItemStatusRules.find(
    (_) => _.currentStatus === subtaskItem.status && _.isCommon === useCommonStatus,
  );
  const [nextStatusList, setNextStatusList] = useState<
    SubtaskItemStatusType[] | undefined
  >();

  const [previousStatus, setPreviousStatus] = useState<
    SubtaskItemStatusType[] | undefined
  >();

  useEffect(() => {
    if (!subtaskItem.smSubtask?.useCommonStatus) {
      if (subtaskItem.smSubtask?.smSubtaskType?.code === 'PROOF') {
        setNextStatusList(subtaskItemStatusRule?.subtaskTypeProof?.nextStatus);
      } else if (subtaskItem.smSubtask?.smSubtaskType?.code === 'MOCKUP') {
        setNextStatusList(subtaskItemStatusRule?.subtaskTypeMockup?.nextStatus);
      } else if (subtaskItem.smSubtask?.smSubtaskType?.code === 'VERIFY DATA') {
        setNextStatusList(subtaskItemStatusRule?.subtaskTypeVerifyData?.nextStatus);
      } else {
        //get previous status of artwork subtask type.
        const prevStatus = subtaskItemStatusRule?.subtaskTypeArtwork?.previousStatus;
        setPreviousStatus(prevStatus);
        //get next status of artwork subtask type.
        const nextStatus = subtaskItemStatusRule?.subtaskTypeArtwork?.nextStatus;
        setNextStatusList(nextStatus);
      }
    } else {
      const commonStatus = subtaskItemStatusRules.find(
        (itemStatus) =>
          itemStatus.currentStatus === subtaskItem.status && itemStatus.isCommon === true,
      );
      setNextStatusList(commonStatus?.nextStatus);
    }
  }, [subtaskItem, subtaskItemStatusRule]);

  const handleOkInArtworkLinkDialog = (subtaskItems: ISubtaskItem[]) => {
    const selectedSubtaskItem = subtaskItems[0];
    const selectedSubtaskItemClone = { ...selectedSubtaskItem };
    selectedSubtaskItemClone.status = 'WAITING_FOR_SALES';
    dispatch(updateSubtaskItemStatus(selectedSubtaskItemClone));
    setOpenArtworkLinkDialog(false);
  };

  return (
    <>
      <SubtaskItemCustomerRejectDialog
        open={openSubtaskItemCustRejectDialog}
        onClose={(reason: string) => {
          console.log('reason for customer reject: ', reason);
          setOpenSubtaskItemCustRejectDialog(false);
          if (reason) {
            const selectedSubtaskItemClone = { ...subtaskItem };
            selectedSubtaskItemClone.status = 'CUSTOMER_REJECT';
            selectedSubtaskItemClone.reason = reason;
            dispatch(updateSubtaskItemStatus(selectedSubtaskItemClone));
          }
        }}
      />

      <SubtaskItemCloseDialog
        subtaskItems={[subtaskItem]}
        open={openSubtaskItemCloseDialog}
        onClose={() => {
          setOpenSubtaskItemCloseDialog(false);
        }}
      />
      <SubtaskItemDoneDialog
        subtaskItems={[subtaskItem]}
        open={openSubtaskItemDoneDialog}
        onClose={() => {
          setOpenSubtaskItemDoneDialog(false);
        }}
      />
      <ArtworkLinkDialog
        open={openArtworkLinkDialog}
        onOk={handleOkInArtworkLinkDialog}
        subtaskItems={[subtaskItem]}
      />
      {(!!previousStatus?.length || !!nextStatusList?.length) && (
        <Box className={classes.actionPanel}>
          {!!previousStatus?.length && (
            <Box className={classes.actionGroup}>
              <Typography className={classes.actionGroupLabel}>
                Previous Status
              </Typography>
              <Box className={classes.actionButtonRow}>
                {previousStatus.map((prevStatus, index) => (
                  <Button
                    key={index}
                    className={`${classes.actionButton} ${classes.previousActionButton}`}
                    variant="contained"
                    size="small"
                    onClick={() => onClickChangeStatus(subtaskItem, prevStatus)}
                    startIcon={<NavigateBeforeIcon />}
                  >
                    {SubtaskItemStatusLabel[prevStatus]}
                  </Button>
                ))}
              </Box>
            </Box>
          )}

          {!!nextStatusList?.length && (
            <Box className={classes.actionGroup}>
              <Typography className={classes.actionGroupLabel}>Next Status</Typography>
              <Box className={classes.actionButtonRow}>
                {nextStatusList.map((nextStatus, index) => (
                  <Button
                    key={index}
                    className={`${classes.actionButton} ${classes.nextActionButton}`}
                    variant="contained"
                    size="small"
                    onClick={() => onClickChangeStatus(subtaskItem, nextStatus)}
                    startIcon={<NavigateNextIcon />}
                  >
                    {SubtaskItemStatusLabel[nextStatus]}
                  </Button>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default SubtaskItemActionMenu;
